import { useEffect, useState } from "react"
import { matchPath, Navigate, Outlet, Route, Routes, useLocation, useOutletContext } from "react-router-dom"
import { Navbar, SidebarDrawer } from "@/components"
import Sidebar from "../components-legacy/Sidebar.jsx"
import Error404 from "../error/Error404.jsx"
import ErrorApp from "../error/ErrorApp.jsx"
import { AppProvider } from "../providers/AppProvider.jsx"
import AppPaywallHost from "../components/AppPaywallHost.jsx"
import Feedback from "./Feedback.jsx"
import SignOut from "./SignOut.jsx"
import { Strategy } from "./Strategy.jsx"
import { useApp } from "../hooks/useApp.jsx"
import Subscribe from "./Subscribe.jsx"
import SubscribeComplete from "./SubscribeComplete.jsx"
import PaymentCheckout from "./PaymentCheckout.jsx"
import Settings from "./Settings.jsx"
import SettingsProfile from "./SettingsProfile.jsx"
import SettingsContact from "./SettingsContact.jsx"
import ChangeSubscription from "./ChangeSubscription.jsx"
import SwitchMarketsPlanChange from "./SwitchMarketsPlanChange.jsx"
import BotNew from "../bot/BotNew.tsx"
import BotCreate from "../bot/BotCreate.tsx"
import BotDetail from "../bot/BotDetail.jsx"
import BotLive from "../bot/BotLive.tsx"
import BotBacktestPage from "./BotBacktestPage.tsx"
import BotCodePage from "./BotCodePage.tsx"
import HomeRedirect from "./HomeRedirect.tsx"

const MOBILE_BREAKPOINT = 1100
const MOBILE_MEDIA_QUERY = `(max-width: ${MOBILE_BREAKPOINT - 1}px)`

// PageWrapper provides the proper container for each page type
// - scrollable: true = traditional scrollable page (Settings, etc.)
// - scrollable: false/undefined = fixed layout page (BotDetail, etc.)
function PageWrapper({ children, scrollable = false }) {
  if (scrollable) {
    return (
      <div className="w-full h-full overflow-auto">
        {children}
      </div>
    )
  }

  return (
    <div className="relative w-full h-full overflow-hidden">
      {children}
    </div>
  )
}

function SidebarLayout() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(MOBILE_MEDIA_QUERY).matches : false
  )
  const [drawerOpen, setDrawerOpen] = useState(false)
  const location = useLocation()

  const botDetailMatch = matchPath("/strategy/:bid", location.pathname)
  const ownsTopBar =
    botDetailMatch !== null &&
    botDetailMatch.params.bid !== "new" &&
    botDetailMatch.params.bid !== "create" &&
    botDetailMatch.params.bid !== "live"

  useEffect(() => {
    if (typeof window === "undefined") return
    const mq = window.matchMedia(MOBILE_MEDIA_QUERY)
    const handler = (e) => {
      setIsMobile(e.matches)
      if (!e.matches) setDrawerOpen(false)
    }
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])

  return (
    <div className="flex h-dvh bg-card overflow-hidden">
      {isMobile ? (
        <SidebarDrawer open={drawerOpen} onOpenChange={setDrawerOpen}>
          <Sidebar mobileMode onAfterSelect={() => setDrawerOpen(false)} />
        </SidebarDrawer>
      ) : (
        <Sidebar />
      )}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {isMobile && !ownsTopBar && (
          <Navbar
            isMobile
            onMenuClick={() => setDrawerOpen((open) => !open)}
          />
        )}
        <main className="flex-1 overflow-hidden relative min-w-0">
          <Outlet context={{ isMobile, drawerOpen, setDrawerOpen }} />
        </main>
      </div>
    </div>
  )
}

export default function Home() {
  return (
    <AppProvider>
      <AppPaywallHost />
      <Routes>
        <Route element={<NoSubscriptionGuard />}>
          <Route path="/subscribe" element={<PageWrapper scrollable><Subscribe /></PageWrapper>} />
        </Route>
        <Route path="/checkout/complete" element={<PageWrapper scrollable><SubscribeComplete /></PageWrapper>} />
        <Route path="/payment" element={<PageWrapper scrollable><PaymentCheckout /></PageWrapper>} />

        {/* Support-only, by URL: no link anywhere in the app. Standalone and
            outside SubscriptionGuard on purpose — the page reports "no active
            plan" itself rather than bouncing to /subscribe. */}
        <Route path="/subscription/change/switch-markets" element={<PageWrapper scrollable><SwitchMarketsPlanChange /></PageWrapper>} />

        <Route element={<SidebarLayout />}>
          <Route element={<SubscriptionGuard />}>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/archived/strategy/:sid/*" element={<PageWrapper scrollable><Strategy /></PageWrapper>} />

            <Route path="/strategy/new" element={<PageWrapper><BotNew /></PageWrapper>} />
            <Route path="/strategy/create" element={<PageWrapper><BotCreate /></PageWrapper>} />
            <Route path="/strategy/live" element={<PageWrapper scrollable><BotLive /></PageWrapper>} />
            <Route path="/strategy/:bid" element={<PageWrapper><BotDetail /></PageWrapper>} />

            <Route path="/billing" element={<PageWrapper scrollable><Settings /></PageWrapper>} />
            <Route path="/subscription/change" element={<PageWrapper scrollable><ChangeSubscription /></PageWrapper>} />

            <Route path="/profile" element={<PageWrapper scrollable><SettingsProfile /></PageWrapper>} />
            <Route path="/contact" element={<PageWrapper scrollable><SettingsContact /></PageWrapper>} />
          </Route>
          <Route path="/backtest/:btid" element={<PageWrapper scrollable><BotBacktestPage /></PageWrapper>} />
          <Route path="/code/:cid/backtests" element={<PageWrapper scrollable><BotCodePage /></PageWrapper>} />
          <Route path="/code/:cid" element={<PageWrapper scrollable><BotCodePage /></PageWrapper>} />
          <Route path="/feedback/*" element={<PageWrapper scrollable><Feedback /></PageWrapper>} />
          <Route path="/signout" element={<PageWrapper scrollable><SignOut /></PageWrapper>} />
          <Route path="/err/app" element={<PageWrapper scrollable><ErrorApp /></PageWrapper>} />
          <Route path="*" element={<PageWrapper scrollable><Error404 /></PageWrapper>} />
        </Route>
      </Routes>
    </AppProvider>
  )
}

function SubscriptionGuard() {
  const { session } = useApp()
  const context = useOutletContext()

  if (session && !session.subscription) {
    return <Navigate to="/subscribe" />
  }

  return <Outlet context={context} />
}

function NoSubscriptionGuard() {
  const { session } = useApp()
  const context = useOutletContext()

  if (session && session.subscription) {
    return <Navigate to="/billing" />
  }

  return <Outlet context={context} />
}
